import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { TurnstileWidget, TURNSTILE_SITE_KEY } from '@/components/TurnstileWidget';

interface ContactFormProps {
  showTitle?: boolean;
  className?: string;
}

export const ContactForm = ({ showTitle = false, className = '' }: ContactFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    department: '',
    message: '',
  });
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileReset, setTurnstileReset] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast({
        title: t('contact.form.errorTitle'),
        description: t('contact.form.captchaMessage'),
        variant: 'destructive',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, website: honeypot, turnstileToken }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to send message');
      }
      toast({
        title: t('contact.form.successTitle'),
        description: t('contact.form.successMessage'),
      });
      setFormData({ name: '', email: '', phone: '', company: '', department: '', message: '' });
    } catch (err: any) {
      toast({
        title: t('contact.form.errorTitle'),
        description:
          err?.message === 'captcha_failed'
            ? t('contact.form.captchaMessage')
            : t('contact.form.errorMessage'),
        variant: 'destructive',
      });
    } finally {
      // Turnstile tokens are single-use: reset for a fresh token after every attempt.
      setTurnstileReset((n) => n + 1);
      setIsSubmitting(false);
    }
  };

  const formFields = [
    { name: 'name', labelKey: 'contact.form.name', type: 'text', required: true },
    { name: 'email', labelKey: 'contact.form.email', type: 'email', required: true },
    { name: 'phone', labelKey: 'contact.form.phone', type: 'tel', required: false },
    { name: 'company', labelKey: 'contact.form.company', type: 'text', required: false },
  ];

  const departmentOptions = [
    { value: 'general', labelKey: 'contact.form.departments.general' },
    { value: 'evex', labelKey: 'contact.form.departments.evex' },
    { value: 'focus', labelKey: 'contact.form.departments.focus' },
    { value: 'whitelight', labelKey: 'contact.form.departments.whitelight' },
    { value: 'scurve', labelKey: 'contact.form.departments.scurve' },
    { value: 'investors', labelKey: 'contact.form.departments.investors' },
    { value: 'careers', labelKey: 'contact.form.departments.careers' },
  ];

  return (
    <div className={className}>
      {showTitle && (
        <>
          <h3 className="text-xl font-bold text-foreground mb-2">{t('contact.form.title')}</h3>
          <p className="text-muted-foreground text-sm mb-8">{t('contact.form.subtitle')}</p>
        </>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {formFields.map((field) => (
            <motion.div key={field.name} className="relative" whileTap={{ scale: 0.99 }}>
              <label
                className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                  focusedField === field.name || formData[field.name as keyof typeof formData]
                    ? 'top-2 text-xs text-primary'
                    : 'top-4 text-sm text-muted-foreground'
                }`}
              >
                {t(field.labelKey)} {field.required && '*'}
              </label>
              <input
                type={field.type}
                required={field.required}
                data-testid={`input-contact-${field.name}`}
                value={formData[field.name as keyof typeof formData]}
                onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                onFocus={() => setFocusedField(field.name)}
                onBlur={() => setFocusedField(null)}
                className="w-full px-4 pt-6 pb-2 bg-background/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary focus:shadow-[0_0_15px_hsl(290_50%_55%_/_0.2)] transition-all duration-300"
              />
            </motion.div>
          ))}
        </div>

        <motion.div className="relative" whileTap={{ scale: 0.99 }}>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('contact.form.department')} *
          </label>
          <select
            required
            data-testid="select-contact-department"
            value={formData.department}
            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary focus:shadow-[0_0_15px_hsl(290_50%_55%_/_0.2)] transition-all duration-300"
          >
            <option value="">{t('contact.form.selectDepartment')}</option>
            {departmentOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
            ))}
          </select>
        </motion.div>

        <motion.div className="relative" whileTap={{ scale: 0.99 }}>
          <label
            className={`absolute left-4 transition-all duration-300 pointer-events-none ${
              focusedField === 'message' || formData.message
                ? 'top-2 text-xs text-primary'
                : 'top-4 text-sm text-muted-foreground'
            }`}
          >
            {t('contact.form.message')} *
          </label>
          <textarea
            required
            rows={5}
            data-testid="textarea-contact-message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            onFocus={() => setFocusedField('message')}
            onBlur={() => setFocusedField(null)}
            className="w-full px-4 pt-6 pb-2 bg-background/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary focus:shadow-[0_0_15px_hsl(290_50%_55%_/_0.2)] transition-all duration-300 resize-none"
          />
        </motion.div>

        {/* Honeypot: hidden from real users, bots tend to fill every field */}
        <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
          <label htmlFor="contact-website">Website</label>
          <input
            id="contact-website"
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <TurnstileWidget onToken={setTurnstileToken} resetSignal={turnstileReset} />

        <motion.button
          type="submit"
          disabled={isSubmitting}
          data-testid="button-contact-submit"
          className="btn-primary w-full group disabled:opacity-50 disabled:cursor-not-allowed"
          whileHover={isSubmitting ? {} : { scale: 1.02 }}
          whileTap={isSubmitting ? {} : { scale: 0.98 }}
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('contact.form.sending')}
              </>
            ) : (
              <>
                {t('contact.form.submit')}
                <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </>
            )}
          </span>
        </motion.button>
      </form>
    </div>
  );
};
